/**
 * The Director's wording documents (prompt 1.4.1): docs/EMAIL-WORDING-2026-10.md and any
 * Downloads/5-Email-Wording/*.md written the same way —
 *
 *   ## A. <group>                      (a group whose title says "not built yet" is for a later version)
 *   ### A1 · `TEMPLATE_CODE` — title
 *   *For:* … *Buttons:* **Label** · **Label** · Label.
 *   **Subject:** …
 *   **Heading:** …                     (optional)
 *   <body>
 *   **Español — Asunto:** …
 *   **Encabezado:** …
 *   <body>
 *   *Botones:* **Etiqueta** · **Etiqueta**
 *   ---
 *
 * The words are taken as written. The one change is the document's own line wrapping: its paragraphs are
 * wrapped at about 105 columns, and a wrapped line (80 characters or more) is joined to the next with a
 * space, as Markdown reads it. Short lines (the signature, "Warm regards,") and the `> ` detail lines keep
 * their line breaks.
 *
 * The heading has no column on SM_EmailTemplates (the app never adds one): it is kept as the first line
 * of the body, written `# Heading`, and the sender shows it as the email's heading.
 */

export interface WordingPart {
  subject: string;
  heading: string | null;
  body: string;
  /** The button labels the document lists (bold or not), in order. */
  buttons: string[];
}

export interface WordingSection {
  code: string;
  /** "A1", "C12"… */
  ref: string;
  /** The `##` group the section is under. */
  group: string;
  /** Under a group marked "not built yet": the document is its source, the app does not load it. */
  later: boolean;
  en: WordingPart;
  /** null: English only by design (the internal memos, D1–D6). */
  es: WordingPart | null;
}

const WRAP_AT = 80;

/** Joins the document's soft wraps; keeps the paragraph breaks, the detail lines and the short lines. */
export function unwrapBody(lines: string[]): string {
  const out: string[] = [];
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    const prev = out.length ? out[out.length - 1]! : null;
    const isBlock = /^(> |>$|- )/.test(line);
    if (prev !== null && prev !== '' && line !== '' && !isBlock && !prev.startsWith('> ') && prev.length >= WRAP_AT) {
      out[out.length - 1] = `${prev} ${line.trim()}`;
    } else out.push(line);
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

const SUBJECT_EN = /^\*\*Subject:\*\*\s*(.*)$/;
const HEADING_EN = /^\*\*Heading:\*\*\s*(.*)$/;
const SUBJECT_ES = /^\*\*Español\s*[—–-]\s*Asunto:\*\*\s*(.*)$/;
const HEADING_ES = /^\*\*Encabezado:\*\*\s*(.*)$/;
const BUTTONS_ES = /^\*(Botones|Botón):\*\s*(.*)$/;
const SPANISH_NOT_NEEDED = /^\*\(Spanish not needed/i;

function buttonsFrom(text: string): string[] {
  const t = text.trim().replace(/\.$/, '');
  if (!t || /^none\b/i.test(t)) return [];
  return t
    .split(/\s+·\s+/)
    .map((x) => x.replace(/\*\*/g, '').replace(/\.$/, '').trim())
    .filter(Boolean);
}

/** The English buttons from the section's lead paragraph: "*Buttons:* … " up to the next "*Label:*". */
function leadButtons(lead: string): string[] {
  const m = /\*Buttons:\*\s*(.+?)(?=\s\*[A-Z][a-z]+:\*|$)/.exec(lead);
  return m ? buttonsFrom(m[1]!) : [];
}

function part(lines: string[], subjectRe: RegExp, headingRe: RegExp, stop: (l: string) => boolean): { part: Omit<WordingPart, 'buttons'>; rest: number } | null {
  const si = lines.findIndex((l) => subjectRe.test(l));
  if (si < 0) return null;
  const subject = subjectRe.exec(lines[si]!)![1]!.trim();
  let i = si + 1;
  let heading: string | null = null;
  if (i < lines.length && headingRe.test(lines[i]!)) {
    heading = headingRe.exec(lines[i]!)![1]!.trim();
    i++;
  }
  const body: string[] = [];
  for (; i < lines.length && !stop(lines[i]!); i++) body.push(lines[i]!);
  return { part: { subject, heading, body: unwrapBody(body) }, rest: i };
}

/** Every `### … \`CODE\` …` section of a wording document, in order. */
export function parseWordingDoc(md: string): WordingSection[] {
  const lines = md.split(/\r?\n/);
  const out: WordingSection[] = [];
  let group = '';
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (/^## /.test(line)) {
      group = line.slice(3).trim();
      i++;
      continue;
    }
    const head = /^###\s+(.*?)`([A-Z][A-Z0-9_]*)`/.exec(line);
    if (!head) {
      i++;
      continue;
    }
    let end = i + 1;
    while (end < lines.length && !/^(#{2,3} |---\s*$)/.test(lines[end]!)) end++;
    const sec = lines.slice(i + 1, end);
    i = end;
    const code = head[2]!;
    const ref = (/^([A-Z]\d+)/.exec(head[1]!.trim()) ?? [])[1] ?? '';
    const si = sec.findIndex((l) => SUBJECT_EN.test(l));
    if (si < 0) continue;
    const lead = sec.slice(0, si).join(' ');
    const en = part(sec, SUBJECT_EN, HEADING_EN, (l) => SUBJECT_ES.test(l) || SPANISH_NOT_NEEDED.test(l) || BUTTONS_ES.test(l));
    if (!en) continue;
    const esLines = sec.slice(en.rest);
    const es = part(esLines, SUBJECT_ES, HEADING_ES, (l) => BUTTONS_ES.test(l));
    const esButtonsLine = esLines.find((l) => BUTTONS_ES.test(l));
    out.push({
      code,
      ref,
      group,
      later: /not built yet/i.test(group),
      en: { ...en.part, buttons: leadButtons(lead) },
      es: es ? { ...es.part, buttons: esButtonsLine ? buttonsFrom(BUTTONS_ES.exec(esButtonsLine)![2]!) : [] } : null,
    });
  }
  return out;
}

/**
 * Section E of the document ("New in 1.5.0 — not built yet") is built in 1.5.0: its five codes are built-in
 * templates now and load like the rest. A later group's other sections stay in the document.
 */
export const BUILT_IN_FROM_LATER: readonly string[] = ['TECH_TICKET', 'TECH_BOOKED', 'TECH_REMINDER', 'CLOSEOUT_REMINDER', 'VOL_ACTIVITY_REQUEST', 'VOL_SCHEDULE_INVITE', 'SURVEY_INVITE', 'EVENT_VOLUNTEER_REQUEST', 'EVENT_VOLUNTEER_MONTH', 'EVENT_REMINDER_VOLUNTEER', 'VOL_READING_THANKS', 'VOL_READING_REPLY', 'EVENT_HELPER_THANKS', 'PLAIN_NOTE'];

/** A section the app loads: not in a "not built yet" group, or one of the codes built since. */
export function isLoadable(s: Pick<WordingSection, 'later' | 'code'>): boolean {
  return !s.later || BUILT_IN_FROM_LATER.includes(s.code);
}

/** The body as stored: the heading as its first line (`# Heading`), then the words. */
export function composeBody(heading: string | null, body: string): string {
  return heading ? `# ${heading}\n\n${body}` : body;
}

/** The other way: a stored body → its heading (or null) and the words under it. */
export function splitHeading(stored: string): { heading: string | null; body: string } {
  const m = /^# (.+)\r?\n(?:\r?\n)?/.exec(stored);
  if (!m) return { heading: null, body: stored };
  return { heading: m[1]!.trim(), body: stored.slice(m[0].length) };
}
